<template>
  <EditorPanel class="pb-data-panel">
    <template #header>
      <ObjectHeader :icon="panelIcon" />
    </template>

    <template v-if="preset">
      <ReportMessage
        :item="preset"
        :default-state="true"
        :children-state="false"
      >
        {{ translate('pb2.presetPropertiesView.presetReportTitle') }}
      </ReportMessage>
      <ItemProperties>
        <h3>{{ translate('pb2.presetPropertiesView.presetPropertiesTitle') }}</h3>
        <!-- Fields present on all presets -->

        <!-- Preset name -->
        <InputWithLoader
          :label="translate('pb2.presetPropertiesView.presetNameLabel')"
          :placeholder="translate('pb2.presetPropertiesView.presetNamePlaceholder')"
          :value="preset.data.name"
          :disabled="readonly"
          class="pb-half-margin-top"
          @input="set('name', $event)"
        />
        <UiField
          nolabel
          nofocus
          :label="translate('pb2.presetPropertiesView.presetTypeLabel')"
          class="pb-quarter-margin-top"
        >
          <span class="txt-muted txt-right">{{ presetType }}</span>
        </UiField>

        <!-- Make the preset the default preset -->
        <ButtonWithLoader
          v-tooltip="{
            text: translate(preset.data.is_default ? 'pb2.presetPropertiesView.presetIsAlreadyDefaultTooltip' : 'pb2.presetPropertiesView.presetMakeDefaultTooltip'),
          }"
          :label="translate('pb2.presetPropertiesView.presetIsDefaultLabel')"
          :disabled="readonly || preset.data.is_default"
          class="pb-quarter-margin-top"
          @click="set('is_default', true)"
        />

        <!-- Default DMX value of this preset -->
        <InputWithLoader
          :label="translate('pb2.presetPropertiesView.presetDmxDefaultLabel')"
          placeholder="127"
          :type="INPUT_TYPE_COUNTER"
          :value="preset.data.dmx_default"
          :disabled="readonly"
          autoselect
          class="pb-quarter-margin-top"
          @input="set('dmx_default', $event)"
        />

        <!-- If linear, will show the fader (dimmer) in software -->
        <InputWithLoader
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetLinearLabel')"
          :value="preset.data.show_dimmer"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('show_dimmer', $event)"
        />

        <!-- Optional fields depending on preset type-->

        <!-- Unknown -->
        <InputWithLoader
          v-if="isSet(preset.data.prism_type)"
          :label="translate('pb2.presetPropertiesView.presetPrismTypeLabel')"
          placeholder="0"
          :value="preset.data.prism_type"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('prism_type', $event)"
        />

        <!-- The preset can be rotated by a rotate preset on another channel or itself -->
        <InputWithLoader
          v-if="isSet(preset.data.rotary)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetRotaryLabel')"
          :value="preset.data.rotary"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('rotary', $event)"
        />

        <!-- The preset rotates/shakes itself based on it's value -->
        <!-- Incompatible with target ? needs rotary ? -->
        <InputWithLoader
          v-if="isSet(preset.data.self_rotary)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetSelfRotaryLabel')"
          :value="preset.data.self_rotary"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('self_rotary', $event)"
        />

        <!-- The preset can be shaken by a rotate preset on another channel or itself -->
        <InputWithLoader
          v-if="isSet(preset.data.shake)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetShakeLabel')"
          :value="preset.data.shake"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('shake', $event)"
        />

        <!-- The preset controls an indexed wheel -->
        <InputWithLoader
          v-if="isSet(preset.data.index)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetIndexLabel')"
          :value="preset.data.index"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('index', $event)"
        />

        <!-- The preset bounces a wheel -->
        <InputWithLoader
          v-if="isSet(preset.data.bounce)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetBounceLabel')"
          :value="preset.data.bounce"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('bounce', $event)"
        />

        <!-- The preset makes a random effect -->
        <InputWithLoader
          v-if="isSet(preset.data.random)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetRandomLabel')"
          :value="preset.data.random"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('random', $event)"
        />

        <!-- Effect type (reverse effect/lamp "classic") -->
        <InputWithLoader
          v-if="isSet(preset.data.effect_type)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetEffectTypeLabel')"
          :value="!!preset.data.effect_type"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('effect_type', $event)"
        />

        <!-- Param min/max (number x2, param_min is called "After (seconds)" for lamp) -->
        <InputWithLoader
          v-if="isSet(preset.data.param_min)"
          :label="translate(paramLabels.param_min)"
          placeholder="0"
          :type="INPUT_TYPE_COUNTER"
          :value="preset.data.param_min"
          :disabled="readonly"
          :min="typeof defaultPreset.limits?.min === 'number' ? defaultPreset.limits?.min : null"
          :max="typeof defaultPreset.limits?.max === 'number' ? defaultPreset.limits?.max : null"
          autoselect
          class="pb-quarter-margin-top"
          @input="set('param_min', $event)"
        />
        <InputWithLoader
          v-if="isSet(preset.data.param_max)"
          :label="translate(paramLabels.param_max)"
          placeholder="100"
          :type="INPUT_TYPE_COUNTER"
          :value="preset.data.param_max"
          :disabled="readonly"
          autoselect
          class="pb-quarter-margin-top"
          @input="set('param_max', $event)"
        />

        <!-- Colors -->
        <template v-if="isSet(preset.data.colors)">
          <div
            v-for="(color, idx) in preset.data.colors"
            :key="idx"
            class="pb-preset-color-line pb-quarter-margin-top"
          >
            <ColorInputWithLoader
              class="pb-preset-color-line-input"
              :label="multiColor ? `${translate('pb2.presetPropertiesView.colorLabel')} ${idx + 1}` : translate('pb2.presetPropertiesView.colorLabel')"
              :value="color"
              :disabled="readonly"
              @input="setColor(idx, $event)"
            />
            <ButtonWithLoader
              v-if="multiColor"
              v-tooltip="{ text: translate('pb2.presetPropertiesView.removeColorTooltip') }"
              class="btn-clean btn-hover-error"
              icon="icon-trash"
              :disabled="readonly || !canRemoveColor"
              @click="removeColor(idx)"
            />
          </div>
          <ButtonWithLoader
            v-if="multiColor"
            v-tooltip="{ text: translate('pb2.presetPropertiesView.addColorTooltip') }"
            :label="translate('pb2.presetPropertiesView.addColorLabel')"
            :disabled="readonly || !canAddColor"
            class="pb-quarter-margin-top"
            @click="addColor"
          />
        </template>

        <!-- Target -->
        <SelectFieldWithLoader
          v-if="!isGlobal && isSet(preset.data.target)"
          :label="translate('pb2.presetPropertiesView.presetTargetLabel')"
          :options="potentialTargets"
          :value="preset.data.target"
          :disabled="readonly"
          search
          class="pb-quarter-margin-top"
          @input="set('target', $event)"
        />

        <!-- Advanced stuff we're not sure about -->

        <!-- Unknown - something to do with target ? -->
        <InputWithLoader
          v-if="isSet(preset.data.priority)"
          :type="INPUT_TYPE_SWITCH"
          :label="translate('pb2.presetPropertiesView.presetPriorityLabel')"
          :value="preset.data.priority"
          :disabled="readonly"
          class="pb-quarter-margin-top"
          @input="set('priority', $event)"
        />

        <!-- Level min/max ("DMX range min/max", number x2, is that related to presetRanges ?) -->
        <InputWithLoader
          v-if="isSet(preset.data.level_min)"
          :label="translate('pb2.presetPropertiesView.presetLevelMinLabel')"
          placeholder="0"
          :type="INPUT_TYPE_COUNTER"
          :value="preset.data.level_min"
          :disabled="readonly"
          autoselect
          class="pb-quarter-margin-top"
          @input="set('level_min', $event)"
        />
        <InputWithLoader
          v-if="isSet(preset.data.level_max)"
          :label="translate('pb2.presetPropertiesView.presetLevelMaxLabel')"
          placeholder="255"
          :type="INPUT_TYPE_COUNTER"
          :value="preset.data.level_max"
          :disabled="readonly"
          autoselect
          class="pb-quarter-margin-top"
          @input="set('level_max', $event)"
        />
      </ItemProperties>
      <ButtonGroup
        v-if="preset"
        column
        class="pb-preset-actions"
      >
        <ControlButton
          :to="replaceLink"
          :disabled="readonly"
        >
          <span class="ellipsis">{{ translate('pb2.presetPropertiesView.replacePresetLabel') }}</span>
        </ControlButton>
        <ButtonWithLoader
          :disabled="readonly"
          @click="duplicatePreset"
        >
          <span class="ellipsis">{{ translate('pb2.presetPropertiesView.duplicatePresetLabel') }}</span>
        </ButtonWithLoader>
        <ButtonWithLoader
          v-if="canBeDeleted"
          class="btn-error"
          :disabled="readonly"
          @click="openConfirm"
        >
          <span class="ellipsis">{{ translate('pb2.presetPropertiesView.deletePresetLabel') }}</span>
        </ButtonWithLoader>
      </ButtonGroup>
      <ConfirmDialog
        v-bind="control"
        confirm-style="error"
        confirm-text="pb2.presetPropertiesView.presetDeleteConfirmLabel"
        :dialog-title="translate('pb2.presetPropertiesView.presetDeleteConfirmTitle')"
      >
        {{ translate('pb2.presetPropertiesView.presetDeleteConfirmMessage') }} "{{ preset.linkName }}" ?
      </ConfirmDialog>
    </template>
  </EditorPanel>
</template>

<script setup>
import { computed } from 'vue'

import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import { INPUT_TYPE_COUNTER, INPUT_TYPE_SWITCH } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'
import useCloseOthers from '@nicolaudie-group/ng-ui-kit/composables/useCloseOthers'
import useConfirmDialog from '@nicolaudie-group/ng-ui-kit/composables/useConfirmDialog'

// Import stores
import usePresetStore from '@/stores/preset.store'

// Import composables
import useIsGlobal from '@/composables/useIsGlobal'
import useIsSet from '@/composables/useIsSet'

// Import components
import InputWithLoader from '@/components/InputWithLoader.vue'
import EditorPanel from '@/components/EditorPanel/EditorPanel.vue'
import { presetHelpers } from '@/helpers/profile'
import ReportMessage from '@/components/ReportMessage/ReportMessage.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ItemProperties from '@/components/ItemProperties/ItemProperties.vue'
import routeNames from '@/router/names'
import useDeleteHotkey from '@/composables/useDeleteHotkey'
import useDestination from '@/composables/useDestination'
import useRouterStore from '@/stores/router.store'
import {
  useConstants,
  useReadonly,
  usePreset,
  useChannels,
} from '@/composables/useProfile'
import ObjectHeader from '@/components/ObjectHeader/ObjectHeader.vue'
import useTranslate from '@/composables/useTranslate'
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue'
import SelectFieldWithLoader from '@/components/SelectFieldWithLoader.vue'
import ColorInputWithLoader from '@/components/ColorInputWithLoader.vue'
import { Color } from '@nicolaudie-group/ls-utilities'

// Initialize composables & stores
const { isGlobal } = useIsGlobal()
const { isSet } = useIsSet()
const presetStore = usePresetStore()
const routerStore = useRouterStore()

const preset = usePreset()
const channels = useChannels()
const readonly = useReadonly()

const { translate } = useTranslate()

const constants = useConstants()

const canBeDeleted = computed(() => !!preset.value)

const replaceLink = useDestination(computed(() => {
  const global = isGlobal.value
  const presetUid = preset.value.data._uid
  const currentRoute = routerStore.route
  if (readonly.value) {
    return currentRoute
  }
  return {
    name: global ? routeNames.ReplaceGlobalPreset : routeNames.ReplaceLocalPreset,
    params: {
      presetUid,
    },
  }
}))

// Handle edition
const edit = (payload) => {
  if (isGlobal.value) {
    return presetStore.editGlobal(payload)
  }
  return presetStore.editLocal(payload)
}
const set = (key, value) => edit({ [key]: value })

/**
 * Minimum number of colors based on the preset_type
 */
const minColor = computed(() => {
  const presetType = preset.value.data.preset_type
  const presetConstants = constants.value.presets
  switch (presetType) {
    // For any color preset (even split ones) the minimum of colors is 1
    // The profile SDK will automatically change the preset_type to match the number of colors
    case presetConstants.types.SSL_PR_COLOR:
    case presetConstants.types.SSL_PR_HALFCOLOR:
    case presetConstants.types.SSL_PR_TRICOLOR:
    case presetConstants.types.SSL_PR_QUADCOLOR:
    case presetConstants.types.SSL_PR_DIMMER: // "Dimmer" presets have a single color
    case presetConstants.types.SSL_PR_DIMMER_FIX: // "Dimmer" presets have a single color
    case presetConstants.types.SSL_PR_GOBO: // "Gobo" presets have a single color
      return 1
    case presetConstants.types.SSL_PR_UNDEF: // No minimum for undef presets
    default:
      return 0
  }
})

/**
 * Maximum number of colors based on the preset_type
 */
const maxColor = computed(() => {
  const presetType = preset.value.data.preset_type
  const presetConstants = constants.value.presets
  switch (presetType) {
    // For any color preset (even split ones) the maximum of colors is 4
    // The profile SDK will automatically change the preset_type to match the number of colors
    case presetConstants.types.SSL_PR_COLOR:
    case presetConstants.types.SSL_PR_HALFCOLOR:
    case presetConstants.types.SSL_PR_TRICOLOR:
    case presetConstants.types.SSL_PR_QUADCOLOR:
    case presetConstants.types.SSL_PR_UNDEF: // Also 4 for undef presets
      return 4
    case presetConstants.types.SSL_PR_DIMMER: // "Dimmer" presets have a single color
    case presetConstants.types.SSL_PR_DIMMER_FIX: // "Dimmer" presets have a single color
    case presetConstants.types.SSL_PR_GOBO: // "Gobo" presets have a single color
      return 1
    // Anything else should not have colors at all
    default:
      return 0
  }
})

const setColor = async (idx, value) => {
  const result = [...preset.value.data.colors]
  if (value) {
    result.splice(idx, 1, value.dec)
  } else {
    result.splice(idx, 1)
  }
  await set('colors', result)
}

const canAddColor = computed(() => (
  preset.value.data.colors.length < maxColor.value
))

const canRemoveColor = computed(() => (
  preset.value.data.colors.length > minColor.value
))

const multiColor = computed(() => (
  maxColor.value - minColor.value > 0
))

const addColor = async () => {
  if (canAddColor.value) {
    await setColor(preset.value.data.colors.length, new Color('#fff'))
  }
}

const removeColor = async (idx) => {
  if (canRemoveColor.value) {
    await setColor(typeof idx === 'number' ? idx : preset.value.data.colors.length - 1)
  }
}

function deletePreset() {
  if (isGlobal.value) {
    return presetStore.deleteGlobal()
  }
  return presetStore.deleteLocal()
}
const {
  control,
  open: openConfirm,
  cancel: cancelConfirm,
  state: deletePresetConfirm,
} = useConfirmDialog(deletePreset)
useCloseOthers(cancelConfirm, deletePresetConfirm)

function duplicatePreset() {
  if (isGlobal.value) {
    return presetStore.duplicateGlobal()
  }
  return presetStore.duplicateLocal()
}

const defaultPreset = computed(() => {
  const defaultPresetUid = preset.value.data.defaultUid
  if (constants.value.presets) {
    const { indexed } = constants.value.presets
    return indexed[defaultPresetUid] || null
  }
  return null
})

const paramLabels = computed(() => {
  if (defaultPreset.value) {
    return {
      /*
      Start with translations for generic units as fallback.
      Should not happen that we have a preset property where there is no
      unit defined in the default presets in the SDK, but just in case.
      */
      ...presetHelpers.unitsTranslations.Generic,
      /*
      And override the translations of the generic units with the translations
      of the actual units of the preset, if they exist
      */
      ...presetHelpers.unitsTranslations[defaultPreset.value.unit],
    }
  }
  // Fallback to the generic units
  return presetHelpers.unitsTranslations.Generic
})

// Compute preset_type name
const presetType = computed(() => {
  const defaultPresetUid = preset.value.data.defaultUid
  if (defaultPreset.value) {
    return defaultPreset.value.name
  }
  return `Preset ${defaultPresetUid}`
})

const panelIcon = computed(() => preset.value && presetHelpers.getIcon(preset.value?.data))

const deleteHotkeyOnRoutes = [
  routeNames.LocalPreset,
  routeNames.GlobalPreset,
]

const deleteHotkeyEnabled = computed(() => !deletePresetConfirm.value)
useDeleteHotkey(deleteHotkeyOnRoutes, deleteHotkeyEnabled, () => {
  if (canBeDeleted.value) {
    openConfirm()
  }
})

const potentialTargets = computed(() => (
  [
    { value: '', label: translate('pb2.presetPropertiesView.noTargetOption') },
    ...channels.value.map((channel) => (
      { value: channel.data._uid, label: channel.data.name }
    )),
  ]
))
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

.pb-preset-actions {
  @include flexbox.flex-grow(1);
  @include flexbox.justify-content(flex-end);
  padding-top: pb.$spacing;
}

.pb-preset-color-line {
  @include flexbox.flexbox();
  @include flexbox.align-items(center);

  .pb-color-field {
    // @include flexbox.flex-grow(1);
    margin: 0 !important;
    width: 100%;
  }
}
</style>
